import { validateCPF, validateCNPJ } from "@/lib/custom-validations";
import { z } from "zod";

// Configuration schema - extends generalInformationPFFormSchema but all fields optional for editing
export const configuracoesPFFormSchema = z.object({
  // Personal Information
  completeName: z.string().optional(),
  socialName: z.string().optional(),
  artisticName: z.string().optional(),
  role: z.string().optional(),
  rg: z.string().optional(),
  cpf: z.string().optional().refine((value) => {
    if (!value) return true; // Optional field
    const cleanValue = value.replace(/\D/g, "");
    return cleanValue.length === 11 && validateCPF(cleanValue);
  }, {
    message: "CPF inválido"
  }),
  birthDate: z.union([
    z.date(),
    z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, "Data inválida").transform((str) => {
      const [day, month, year] = str.split("/");
      return new Date(Number(year), Number(month) - 1, Number(day));
    })
  ]).optional(),
  birthPlace: z.string().optional(),
  nationality: z.string().optional(),
  countryEntryDate: z.union([
    z.date(),
    z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, "Data inválida").transform((str) => {
      const [day, month, year] = str.split("/");
      return new Date(Number(year), Number(month) - 1, Number(day));
    })
  ]).optional(),
  maritalStatus: z.string().optional(),
  pronouns: z.string().optional(),
  cellphone: z.string().optional(),
  tshirtNumber: z.string().optional(),

  // Address fields
  residentialAddress: z.string().optional(),
  district: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),

  // Health and prevention fields
  healthPlan: z.string().optional(),
  planCoverage: z.string().optional(),
  emergencyContact: z.string().optional(),
  bloodType: z.string().optional(),
  allergies: z.string().optional(),
  coronavirusVaccination: z.string().optional(),
  dietaryRestrictions: z.string().optional(),

  // Banking data fields
  bank: z.string().optional(),
  branch: z.string().optional(),
  checkingAccount: z.string().optional(),
  pixKey: z.string().optional(),
});

export type ConfiguracoesPFFormData = z.infer<typeof configuracoesPFFormSchema>;

// PJ Configuration schema - all fields optional for editing
export const configuracoesPJFormSchema = z.object({
  // Company Information
  razaoSocial: z.string().optional(),
  cnpj: z.string().optional().refine((value) => {
    if (!value) return true; // Optional field
    const cleanValue = value.replace(/\D/g, "");
    return cleanValue.length === 14 && validateCNPJ(cleanValue);
  }, {
    message: "CNPJ inválido"
  }),
  enderecoEmpresarial: z.string().optional(),
  bairroPJ: z.string().optional(),
  cidadeEstadoPJ: z.string().optional(),
  cepPJ: z.string().optional(),
  cnaes: z.string().optional(),
  inscricaoMunicipal: z.string().optional(),

  // Banking Information
  bancoPJ: z.string().optional(),
  agenciaPJ: z.string().optional(),
  contaCorrentePJ: z.string().optional(),
  chavePixPJ: z.string().optional(),
});

export type ConfiguracoesPJFormData = z.infer<typeof configuracoesPJFormSchema>;

// Documents schema - all file fields optional
export const configuracoesDocumentsSchema = z.object({
  contratoSocial: z.instanceof(File).optional(),
  comprovanteEnderecoPJ: z.instanceof(File).optional(),
  cartaoCNPJ: z.instanceof(File).optional(),
  comprovanteContaBancariaPJ: z.instanceof(File).optional(),
  documentoFotoRGCNH: z.instanceof(File).optional(),
  comprovanteEnderecoPF: z.instanceof(File).optional(),
  drtOuSimilar: z.instanceof(File).optional(),
  carteiraVacinacao: z.instanceof(File).optional(),
});

export type ConfiguracoesDocumentsData = z.infer<typeof configuracoesDocumentsSchema>;

// Perfil schema - user profile settings
export const perfilFormSchema = z.object({
  username: z.string().min(3, "Mínimo 3 caracteres").max(50, "Máximo 50 caracteres"),
  bio: z.string().max(500, "Máximo 500 caracteres").optional().or(z.literal("")),
  profileImage: z.instanceof(File).optional(),
  facebook: z.string().url("URL inválida do Facebook").optional().or(z.literal("")),
  twitter: z.string().url("URL inválida do X").optional().or(z.literal("")),
  linkedin: z.string().url("URL inválida do LinkedIn").optional().or(z.literal("")),
  instagram: z.string().max(100, "Máximo 100 caracteres").optional().or(z.literal("")), // @usuario format
});

export type PerfilFormData = z.infer<typeof perfilFormSchema>;

// Alterar Senha schema - password change with validation
export const alterarSenhaSchema = z.object({
  oldPassword: z.string().min(1, "Senha antiga obrigatória"),
  newPassword: z.string()
    .min(8, "Mínimo 8 caracteres")
    .regex(/[A-Z]/, "Deve conter pelo menos uma letra maiúscula")
    .regex(/[a-z]/, "Deve conter pelo menos uma letra minúscula")
    .regex(/[0-9]/, "Deve conter pelo menos um número")
    .regex(/[^A-Za-z0-9]/, "Deve conter pelo menos um caractere especial"),
  confirmPassword: z.string().min(1, "Confirmação de senha obrigatória"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "As senhas não conferem",
  path: ["confirmPassword"],
});

export type AlterarSenhaFormData = z.infer<typeof alterarSenhaSchema>;

// Permission levels enum
export const PermissionLevel = {
  FULL_ACCESS: 'full_access',
  VIEW_ONLY: 'view_only',
  NO_ACCESS: 'no_access',
} as const;

export type PermissionLevelType = typeof PermissionLevel[keyof typeof PermissionLevel];

// Permissões schema - 8 permission fields for cadastros section
export const permissionsFormSchema = z.object({
  registerNewMembers1: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers2: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers3: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers4: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers5: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers6: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers7: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
  registerNewMembers8: z.enum(['full_access', 'view_only', 'no_access']).default('full_access'),
});

export type PermissionsFormData = z.infer<typeof permissionsFormSchema>;
